/**
 * Referral 관련 유틸리티
 * 쿠키 저장/읽기, 레퍼럴 링크 생성
 */

export const REFERRAL_CODE_COOKIE = 'luckyon_referral';

/**
 * 쿠키에서 추천인 코드 조회
 */
export const getReferralCodeFromCookie = (): string | null => {
  const match = document.cookie.match(new RegExp(`${REFERRAL_CODE_COOKIE}=([^;]+)`));
  return match ? match[1] : null;
};

/**
 * 쿠키에 추천인 코드 저장 (7일 만료)
 */
export const saveReferralCodeToCookie = (code: string): void => {
  const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7일 후
  const isSecure = window.location.protocol === 'https:';
  const secureFlag = isSecure ? '; Secure' : '';
  document.cookie = `${REFERRAL_CODE_COOKIE}=${code}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${secureFlag}`;
};

/**
 * 쿠키에서 추천인 코드 삭제
 */
export const clearReferralCodeCookie = (): void => {
  document.cookie = `${REFERRAL_CODE_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * URL 파라미터에서 추천인 코드 추출
 */
export const extractReferralCodeFromURL = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');

  if (code && code.length === 8) {
    return code;
  }

  return null;
};

// 레퍼럴 링크 캐시
const referralLinkCache = new Map<string, string>();

/**
 * 현재 URL 기반으로 레퍼럴 링크 생성 (메모이제이션 적용)
 */
export const generateReferralLink = (referralCode: string): string => {
  // 캐시 확인
  const cacheKey = `${window.location.origin}:${referralCode}`;
  const cached = referralLinkCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  // 새로 생성
  const baseUrl = window.location.origin; // http://localhost:3000 또는 https://luckyon.com
  const link = `${baseUrl}/?code=${referralCode}`;

  // 캐시에 저장 (최대 100개 유지)
  if (referralLinkCache.size >= 100) {
    const firstKey = referralLinkCache.keys().next().value;
    if (firstKey) {
      referralLinkCache.delete(firstKey);
    }
  }
  referralLinkCache.set(cacheKey, link);

  return link;
};

/**
 * 레퍼럴 링크를 클립보드에 복사
 */
export const copyReferralLink = async (referralCode: string): Promise<boolean> => {
  try {
    const link = generateReferralLink(referralCode);
    await navigator.clipboard.writeText(link);
    return true;
  } catch (error) {
    // Fallback: 구형 브라우저 지원
    try {
      const textArea = document.createElement('textarea');
      textArea.value = generateReferralLink(referralCode);
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();

      const success = document.execCommand('copy');
      document.body.removeChild(textArea);

      return success;
    } catch (fallbackError) {
      return false;
    }
  }
};

/**
 * 추천인 코드를 클립보드에 복사
 */
export const copyReferralCode = async (referralCode: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(referralCode);
    return true;
  } catch (error) {
    // Fallback
    try {
      const textArea = document.createElement('textarea');
      textArea.value = referralCode;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();

      const success = document.execCommand('copy');
      document.body.removeChild(textArea);

      return success;
    } catch (fallbackError) {
      return false;
    }
  }
};
